import { useShown } from "@/context/SiteModeContext";
import { ruleFor } from "@/siteMap";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useMall } from "@/context/MallContext";
import { SHOP_NAV } from "@/lib/nav";
import { NAV as OFFICE_NAV } from "@/components/account/nav";

/**
 * The site index: every page on 13thav, grouped by the three pillars plus the shop owner's back
 * office. It sits under every footer so nothing on the site is a dead end. Shops are read live,
 * and the back-office column is read from the same menu the back office itself renders.
 */
const WHITE_LABEL = [
    ["/", "White-label home (13thav.com)"], ["/whitelabel#what-you-get", "What you get"], ["/whitelabel#modes", "Wizard Mode & Consignment Mode"],
    ["/whitelabel#pricing", "Plans & pricing"], ["/whitelabel#book-a-call", "Book a 10-minute setup call"], ["/spaces", "Compare the four plans"],
    ["/account/sell", "Open a shop (pick your plan)"], ["/register", "Create an account"], ["/login", "Log in"],
];
const DIRECTORY = [
    ["/directory", "Shop directory (pick a shop)"], ...SHOP_NAV.map((n) => [n.to, n.label]),
    ["/shirts?ready=same_day", "Shirts · Same Day"], ["/shirts?ready=days_3_5", "Shirts · 3–5 Days"], ["/shirts?ready=weeks_2_4", "Shirts · 2–4 Weeks"],
    ["/shirts/dtf", "DTF gang sheet"], ["/shirts/dtf/gang-sheet", "DTF gang sheet builder"], ["/community", "Community board"],
    ["/supplies/sell", "Post a classified ad"], ["/artwork-request", "Artwork request"], ["/website-design", "Website design"],
    ["/services", "Services"], ["/cart", "Cart"], ["/track", "Track an order"],
];
const ACCOUNT = OFFICE_NAV.flatMap((g) => g.items
    .filter((i) => i.to && !i.soon && !i.to.startsWith("/community") && !i.to.startsWith("/wholesale") && !i.to.startsWith("/spaces"))
    .map((i) => [i.to, g.label ? `${g.label} → ${i.label}` : i.label]));

const onMap = (show) => ([to]) => show(ruleFor(to.split(/[?#]/)[0]));

function Col({ title, rows, testid }) {
    return (
        <div data-testid={testid}>
            <p className="border-b-2 border-zinc-700 pb-1 font-display text-sm font-extrabold uppercase tracking-wide text-white">{title}</p>
            <ul className="mt-2 columns-1 gap-6 text-xs text-zinc-400 sm:columns-2">
                {rows.map(([to, label]) => (
                    <li key={to + label} className="break-inside-avoid py-0.5">
                        <Link to={to} className="hover:text-white hover:underline">{label}</Link>
                        <span className="ml-1.5 font-mono text-[11px] text-zinc-600">{to}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function SiteMap() {
    const show = useShown();
    const { floor } = useMall();
    const shops = floor ? ["own_sites", "anchors", "storefronts", "kiosks"].flatMap((k) => floor[k] || []) : [];
    const shopRows = shops.map((s) => [`/stores/${s.slug}`, `${s.name} — shop page`]);
    const [open, setOpen] = useState(false);
    return (
        <section className="dark-block border-t border-zinc-800 bg-zinc-950" data-testid="site-map">
            <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
                <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="site-map-toggle"
                    className="flex w-full items-center justify-between gap-3 text-left">
                    <span>
                        <span className="block font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">Site index · every page on 13thav</span>
                        <span className="mt-1 block text-xs text-zinc-500">13thav has three parts: the white-label website you can rent, the directory of shops that run on it, and each shop's own page. The last column is the shop owner's back office.</span>
                    </span>
                    <ChevronDown size={16} className={`shrink-0 text-zinc-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                </button>
                {open && <div className="mt-6 grid gap-8 lg:grid-cols-2 xl:grid-cols-4" data-testid="site-map-columns">
                    <Col title="1 · White-label" rows={WHITE_LABEL.filter(onMap(show))} testid="site-map-white-label" />
                    <Col title="2 · Directory" rows={DIRECTORY.filter(onMap(show))} testid="site-map-directory" />
                    <Col title={`3 · Shops (${shops.length})`} rows={shopRows} testid="site-map-shops" />
                    <Col title="Shop owner account" rows={ACCOUNT.filter(onMap(show))} testid="site-map-account" />
                </div>}
            </div>
        </section>
    );
}
